<template>
    <PopinButton :mode="PopinMode.Click" @click="handleButtonClick" @element-init="initButtonRoot">
        <slot name="button" v-bind="{isOpened, placement}" />
    </PopinButton>
    <Teleport v-if="isReady" to="body">
        <div
            v-show="isOpened"
            ref="popinContentRef"
            :class="{isOpened, isPositioned, isDisplaying}"
            class="popin-content"
            :style="getComputedPositioning"
        >
            <slot name="content" :is-opened="isOpened" />
        </div>
    </Teleport>
</template>

<script setup lang="ts">
    import type {StyleValue} from 'vue';
    import {computed, ref, useTemplateRef} from 'vue';
    import {eventBus} from '@common/events';
    import type {Placement} from '@floating-ui/dom';
    import {offset} from '@floating-ui/dom';
    import {autoUpdate, flip, shift, useFloating} from '@floating-ui/vue';
    import {nextTick} from '#imports';
    import {timeout, useDetectOutsideClick} from '@malt/utils';
    import {PopinMode} from './Popin.types';
    import PopinButton from './PopinButton';

    const props = withDefaults(
        defineProps<{
            placement?: Placement;
            width?: number;
        }>(),
        {width: 350},
    );

    const emit = defineEmits<{
        open: [];
        close: [];
    }>();

    const isOpened = ref(false);
    const isDisplaying = ref(false);
    const isReady = ref(false);

    // - Refs
    const buttonRef = ref<HTMLElement>();
    const popinContentRef = useTemplateRef('popinContentRef');

    function initButtonRoot(el: HTMLElement): void {
        buttonRef.value = el;
    }

    const computedStyleWidth = computed(() => `${props.width}px`);

    // - Positionning
    const {x, y, strategy, placement, isPositioned, update} = useFloating(buttonRef, popinContentRef, {
        whileElementsMounted: autoUpdate,
        placement: props.placement,
        middleware: [shift(), flip(), offset(2)],
        open: isOpened,
    });

    const getComputedPositioning = computed<StyleValue>(() => ({
        position: strategy.value,
        top: `${y.value ?? 0}px`,
        left: `${x.value ?? 0}px`,
    }));

    // - Click handling
    const {startDetecting, stopDetecting} = useDetectOutsideClick(popinContentRef, closePopin, {immediate: false});

    function handleButtonClick() {
        if (isOpened.value) {
            closePopin();
        } else {
            openPopin();
        }
    }

    // - Display states

    async function openPopin() {
        eventBus.emit('closePopins');
        isReady.value = true;
        await nextTick();
        update();
        isOpened.value = true;
        await nextTick();
        isDisplaying.value = true;
        emit('open');
        startDetecting();
        eventBus.on('closePopins', closePopin);
    }

    async function closePopin() {
        eventBus.off('closePopins', closePopin);
        isDisplaying.value = false;
        await timeout(100);
        isOpened.value = false;
        stopDetecting();
        emit('close');
    }
</script>

<style lang="scss" scoped>
    .popin-content {
        background-color: var(--joy-color-background-default);
        border-width: 0;
        border-style: solid;
        border-color: var(--joy-color-secondary-30);
        border-radius: var(--joy-core-radius-3);
        box-shadow: 0 2px 10px rgb(30 30 30 / 20%);
        min-width: max-content;
        width: v-bind(computedStyleWidth);
        max-width: 100%;
        z-index: var(--joy-core-z-index-fixed-content);
        transform: translateY(-10px);
        opacity: 0;
        transition-property: opacity, transform;
        transition-duration: 0.1s;
        transition-timing-function: ease;

        &.isDisplaying,
        &.isPositionned {
            transform: translateY(0);
            opacity: 1;
        }
    }
</style>
